import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Card, Result, Skeleton, message } from 'antd';
import { getInspectionTemplateById } from '../utils/apiService';
import InspectionFormBuilder from '../components/inspections/InspectionFormBuilder';
import { getInspectionDraft } from '../utils/inspectionCacheUtils';

const mergeTemplateMeta = (outer, inner) => {
  if (!inner || typeof inner !== 'object') return inner;
  if (!outer || typeof outer !== 'object') return inner;

  const meta = {
    inspectionName: outer?.inspectionName || outer?.InspectionName || outer?.templateName || outer?.TemplateName || outer?.name || outer?.Name,
    templateName: outer?.templateName || outer?.TemplateName || outer?.inspectionName || outer?.InspectionName || outer?.name || outer?.Name,
    name: outer?.name || outer?.Name || outer?.inspectionName || outer?.InspectionName || outer?.templateName || outer?.TemplateName,
  };

  const next = { ...inner };
  Object.entries(meta).forEach(([k, v]) => {
    if (!next[k] && v) next[k] = v;
  });
  return next;
};

const coerceJson = (val) => {
  if (!val) return null;
  if (val && typeof val === 'object' && 'data' in val) {
    return coerceJson(val.data);
  }
  if (val && typeof val === 'object' && 'result' in val) {
    return coerceJson(val.result);
  }
  // Prefer nested JSON if present (server returns inspectionItemsJson as string)
  if (
    val
    && typeof val === 'object'
    && ('inspectionItemsJson' in val || 'InspectionItemsJson' in val)
  ) {
    const inner = coerceJson(val.inspectionItemsJson ?? val.InspectionItemsJson);
    return mergeTemplateMeta(val, inner);
  }
  if (typeof val === 'string') {
    try { return coerceJson(JSON.parse(val)); } catch { return null; }
  }
  // If it already looks like the template JSON
  if (val && (val.templates || val.schemaVersion)) return val;
  return null;
};

const InspectionTemplateDetail = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [templateJson, setTemplateJson] = useState(null);
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const online = typeof navigator === 'undefined' ? true : navigator.onLine;
      const draftKey = location.state?.draftKey;

      // If we have a draftKey, always try to load from local draft first (it has user responses)
      if (draftKey) {
        const draft = await getInspectionDraft(draftKey);
        const draftData = draft?.data?.templateJson;
        if (draftData && draftData.templates && draftData.templates.length > 0) {
          // Draft has valid template with user responses - use it
          setTemplateJson(draftData);
          return;
        }
        // If offline and no valid draft, show error
        if (!online) {
          const errorText = 'You are offline and this draft has no cached inspection form.';
          message.error(errorText);
          setLoadError(errorText);
          setTemplateJson(null);
          return;
        }
        // If online but draft is empty/invalid, fall through to load fresh template
      }

      // No draftKey or draft was empty - load fresh template from API
      if (!online) {
        const errorText = 'You are offline and this inspection form is not available on this device.';
        message.error(errorText);
        setLoadError(errorText);
        setTemplateJson(null);
        return;
      }

      const data = await getInspectionTemplateById(templateId);
      const json = coerceJson(data);
      if (!json || !Array.isArray(json.templates) || json.templates.length === 0) {
        throw new Error('The inspection form returned by the server is empty or invalid.');
      }
      const normalized = {
        ...json,
        schemaVersion: json.schemaVersion ?? 1,
      };
      setTemplateJson(normalized);
    } catch (e) {
      console.error('Failed to load template', e);
      const serverMessage = e?.response?.data?.message;
      const errorText = serverMessage || e?.message || 'The inspection form could not be loaded.';
      setTemplateJson(null);
      setLoadError(errorText);
      message.error('Failed to load inspection form');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  return (
    <div style={{ padding: 16 }}>
      {loading ? (
        <Skeleton active />
      ) : loadError || !templateJson ? (
        <Card>
          <Result
            status="warning"
            title="Inspection form could not be loaded"
            subTitle={loadError || 'Please try again.'}
            extra={[
              <Button key="back" onClick={() => navigate('/inspections/newInspections')}>
                Back to inspections
              </Button>,
              <Button key="retry" type="primary" onClick={load}>
                Try again
              </Button>,
            ]}
          />
        </Card>
      ) : (
        <Card>
          <InspectionFormBuilder
            templateJson={templateJson}
            setTemplateJson={setTemplateJson}
            templateId={templateId}
            onSaved={(id, { isCompleted }) => {
              const from = location.state?.from;
              if (isCompleted) {
                navigate(from === 'accidentReports' ? '/accidentReports' : '/inspections');
              } else if (id) {
                navigate(`/inspections/responses/${id}`, {
                  state: { from },
                });
              }
            }}
            draftKey={location.state?.draftKey}
          />
        </Card>
      )}
    </div>
  );
};

export default InspectionTemplateDetail;
